import { ExpectedError } from "../../expectedError";
import { parseUrlInput } from "../normalizeInput";
import { fetchTokenAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { exactUrlToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

const apiBaseUrl = (value?: string) => {
  const url = parseUrlInput(value ?? "");
  if (
    !url ||
    !url.pathname.replace(/\/+$/, "").endsWith("/api") ||
    !exactUrlToRegex(url.href)
  ) {
    return "";
  }

  return url.href.replace(/\/+$/, "");
};

export const deltekVantagepoint = defineConnector({
  identifier: "deltekVantagepoint",
  name: "Deltek Vantagepoint",
  tagLine: "Project-based ERP for professional services firms",
  keywords: "Deltek Vantagepoint ERP",
  logo,
  tags: ["productivity", "data"],
  protocol: "http",
  defaultUrlPattern: ({ apiUrl }) => exactUrlToRegex(apiBaseUrl(apiUrl)),
  auth: (app) => {
    const baseUrl = apiBaseUrl(app.configuration.apiUrl);
    return fetchTokenAuth({
      request: {
        url: baseUrl ? `${baseUrl}/token` : "",
        method: "POST",
        formBody: {
          grant_type: "password",
          Username: app.secret.username,
          Password: app.secret.password,
          Integrated: "N",
          database: app.config.database,
          Client_Id: app.secret.clientId,
          client_secret: app.secret.clientSecret,
        },
      },
      extract: { from: "jsonPath", paths: ["access_token"] },
      apply: { kind: "header", scheme: "Bearer" },
      cache: { ttlSeconds: 3600 },
    });
  },
  connectionTest: {
    protocol: "http",
    request: ({ apiUrl }) => {
      const baseUrl = apiBaseUrl(apiUrl);
      if (!baseUrl) {
        throw new ExpectedError(
          "Enter your Vantagepoint HTTPS API base URL ending in /api, without a username, password, query string, or fragment."
        );
      }
      return {
        url: `${baseUrl}/security/self`,
        method: "GET",
        headers: { "Content-Type": "application/json" },
        expectedStatus: 200,
        expectedBodyContains: ['"Username"', '"DisplayUsername"'],
      };
    },
  },
  usageHint: ({ apiUrl }) => {
    const baseUrl = apiBaseUrl(apiUrl);
    return `Use the Vantagepoint REST API${baseUrl ? ` at ${baseUrl}` : " at your configured API base URL"}. Bearer authentication is added automatically. Available modules and records depend on the user’s Vantagepoint permissions. Other Deltek product APIs are not included.`;
  },
  steps: [
    {
      title: "Create a Vantagepoint API authorization",
      items: [
        {
          kind: "markdown",
          content:
            "In Vantagepoint, open **Utilities > Integrations > API Authorization** and select **+ New API Authorization**. Enable **Allow Password Grant Type** for this connector’s username-and-password authentication, and enable **Scopes** to honor the user’s role permissions. Complete the form and save, then copy the **Consumer Key**, **Secret**, and **Database Name**. If an API authorization already exists for this integration, check these settings and use its current credentials. See [Deltek’s API authorization settings](https://help.deltek.com/product/Vantagepoint/7.2/st_util_int_and_imp_api_tab_1.html).\n\nUse an enabled Vantagepoint user with only the roles and record access needed for your integration. This connector uses native Vantagepoint username-and-password authentication (`Integrated=N`), not Windows integrated or Microsoft Entra ID/WAAD sign-in.",
        },
      ],
    },
    {
      title: "Enter your Vantagepoint details",
      items: [
        {
          kind: "baseUrl",
          label: "Vantagepoint API base URL",
          required: true,
          placeholder: "https://your-vantagepoint-host/your-installation/api",
          helperText:
            "Enter your deployment’s HTTPS API base URL, including any port, installation path, and the final /api. Credentials are limited to routes under this exact API prefix. Do not use the documentation URL or include a username, password, query string, or fragment. This connector is for Vantagepoint, not other Deltek products.",
          target: { kind: "configuration", field: "apiUrl" },
        },
        {
          kind: "input",
          label: "Database name",
          required: true,
          helperText:
            "Copy Database Name from the Vantagepoint API Authorization form.",
          target: { kind: "configuration", field: "database" },
        },
        {
          kind: "input",
          label: "Consumer key",
          required: true,
          helperText:
            "Copy Consumer Key from the API Authorization form. This is the application’s client ID.",
          target: { kind: "secrets", field: "clientId" },
        },
        {
          kind: "input",
          label: "Client secret",
          required: true,
          helperText: "Copy Secret from the API Authorization form.",
          target: { kind: "secrets", field: "clientSecret" },
        },
        {
          kind: "input",
          label: "Vantagepoint username",
          required: true,
          target: { kind: "secrets", field: "username" },
        },
        {
          kind: "input",
          label: "Vantagepoint password",
          required: true,
          target: { kind: "secrets", field: "password" },
        },
      ],
    },
  ],
});
